import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  CursorArrowRaysIcon, HandRaisedIcon, Square3Stack3DIcon, ArrowsRightLeftIcon, CalculatorIcon,
  CloudIcon, LinkIcon, TrashIcon, QuestionMarkCircleIcon, ArrowDownTrayIcon,
  PlayIcon, StopIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { startSimulation, stopSimulation } from '../store/simulationSlice';

// LA BARRA DEL LLENÇ D'ESTOCS I FLUXOS.
//
// Substitueix `ToolPalette`, que eren 256 px de panell a l'esquerra amb cinc figures que
// s'arrossegaven al llenç, tres plantilles i un importador de fitxers. La mateixa decisió que es
// va prendre al llenç causal el 25/08/2026, i pels mateixos motius: el que es fa servir mentre
// es dibuixa ha de ser a l'alçada dels ulls i el llenç ha d'ocupar l'amplada sencera.
//
// I una diferència de fons: **arrossegar servia per deixar figures, no per traçar fletxes**, i
// en un diagrama d'estocs i fluxos les fletxes són el que hi diu alguna cosa. Amb modes, traçar
// una canonada és clicar l'origen i clicar el destí.

const VELOCITATS = [0.5, 1, 2];

const MODES = [
  ['select', CursorArrowRaysIcon, 'sf.modeSelect'],
  ['pan', HandRaisedIcon, 'sf.modePan'],
  ['stock', Square3Stack3DIcon, 'sf.modeStock'],
  ['flow', ArrowsRightLeftIcon, 'sf.modeFlow'],
  ['converter', CalculatorIcon, 'sf.modeConverter'],
  ['cloud', CloudIcon, 'sf.modeCloud'],
  ['connector', LinkIcon, 'sf.modeConnector'],
  ['delete', TrashIcon, 'sf.modeDelete'],
];

export default function BarraEstocs({ mode, onModeChange, canvasRef, titolModel, peuPDF }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isRunning } = useSelector((state) => state.simulations);
  const [ajustos, setAjustos] = useState({ speed: 1, showValues: true, showParticles: true, showFlows: false });

  useEffect(() => {
    if (canvasRef.current) canvasRef.current.updateSettings(ajustos);
  }, [ajustos, canvasRef]);

  const engega = () => { dispatch(startSimulation()); canvasRef.current?.startSimulation(ajustos); };
  const atura = () => { dispatch(stopSimulation()); canvasRef.current?.stopSimulation(); };
  const reinicia = () => { dispatch(stopSimulation()); canvasRef.current?.resetSimulation(); };

  return (
    <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-1.5 flex-wrap">
      {MODES.map(([clau, Icona, etiqueta]) => (
        <button key={clau} type="button" onClick={() => onModeChange(clau)} title={t(etiqueta)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                      border transition-colors ${mode === clau
            ? 'bg-primary-700 text-white border-primary-700'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
          <Icona className="h-4 w-4" />
          <span className="hidden md:inline">{t(etiqueta)}</span>
        </button>
      ))}

      <span className="w-px h-6 bg-gray-200 mx-1.5" />

      <button type="button" onClick={isRunning ? atura : engega}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border
                    transition-colors ${isRunning
          ? 'bg-danger-600 text-white border-danger-600 hover:bg-danger-700'
          : 'bg-primary-700 text-white border-primary-700 hover:bg-primary-800'}`}>
        {isRunning ? <StopIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
        {isRunning ? t('simulation.stop') : t('simulation.run')}
      </button>

      <button type="button" onClick={reinicia}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border
                   bg-white text-gray-700 border-gray-300 hover:bg-gray-50 transition-colors">
        <ArrowPathIcon className="h-4 w-4" />{t('simulation.reset')}
      </button>

      <div className="flex items-center gap-1 ml-1">
        <span className="text-[11px] text-gray-500">{t('simulation.speed')}</span>
        {VELOCITATS.map((v) => (
          <button key={v} type="button" onClick={() => setAjustos({ ...ajustos, speed: v })}
            className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
              ajustos.speed === v
                ? 'bg-primary-700 text-white border-primary-700'
                : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}>
            {String(v).replace('.', ',')}×
          </button>
        ))}
      </div>

      {/* Els tres interruptors només fan efecte mentre la simulació corre. «Els fluxos al
          gràfic» va a part i apagat per defecte: un estoc en litres i un flux en litres/minut
          no comparteixen ni unitat ni ordre de magnitud, i posar-los al mateix eix diu coses
          que no són. Qui l'encén ja sap què hi fa. */}
      <div className={`flex items-center gap-3 ml-1 ${isRunning ? '' : 'opacity-50'}`}
           title={isRunning ? undefined : t('sim2.nomesCorrent')}>
        {[['showValues', 'sim2.showValues'], ['showParticles', 'sf.particles'],
          ['showFlows', 'sf.chartFlows']].map(([k, etq]) => (
          <label key={k} className="flex items-center gap-1.5 text-[11px] text-gray-600">
            <input type="checkbox" checked={ajustos[k]} disabled={!isRunning}
              onChange={(e) => setAjustos({ ...ajustos, [k]: e.target.checked })}
              className="h-3.5 w-3.5 rounded-md border-gray-300 text-primary-700 focus:ring-primary-600" />
            {t(etq)}
          </label>
        ))}
      </div>

      <span className="w-px h-6 bg-gray-200 mx-1.5" />

      <button type="button" title={t('tools.exportaPDF')}
        onClick={() => canvasRef.current?.exportaPDF(titolModel, peuPDF)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                   border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 transition-colors">
        <ArrowDownTrayIcon className="h-4 w-4" />
        <span className="hidden lg:inline">PDF</span>
      </button>

      <button type="button" onClick={() => canvasRef.current?.obreAjuda()}
        title={t('sfAjuda.title')} aria-label={t('sfAjuda.title')}
        className="ml-auto text-gray-500 hover:text-gray-700 p-1 rounded-md hover:bg-gray-100">
        <QuestionMarkCircleIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
